package songwritersnotebook.app

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.ui.tooling.preview.Preview
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.rememberNavController
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.lifecycle.viewmodel.compose.viewModel
import songwritersnotebook.app.screens.HomeScreen
import songwritersnotebook.app.screens.EditorScreen
import songwritersnotebook.app.screens.StudioScreen
import songwritersnotebook.app.screens.SettingsScreen
import songwritersnotebook.app.screens.TutorialScreen
import songwritersnotebook.app.screens.SubscriptionScreen
import songwritersnotebook.app.screens.PrivacyPolicyScreen
import songwritersnotebook.app.screens.TermsOfServiceScreen
import songwritersnotebook.app.components.WebFooter
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.navigation.AppRoutes
import songwritersnotebook.app.di.PlatformDependencyProvider
import songwritersnotebook.app.viewmodels.MainViewModel
import songwritersnotebook.app.viewmodels.HomeViewModel
import songwritersnotebook.app.viewmodels.EditorViewModel
import songwritersnotebook.app.viewmodels.StudioViewModel
import songwritersnotebook.app.viewmodels.SettingsViewModel

import songwritersnotebook.app.ui.theme.SongwritersNotebookTheme

import songwritersnotebook.app.tutorial.TutorialOverlay
import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.billing.BillingClientWrapper

import songwritersnotebook.app.screens.RoadmapScreen
import songwritersnotebook.app.screens.AuthScreen
import songwritersnotebook.app.viewmodels.RoadmapViewModel
import songwritersnotebook.app.utils.PlatformUtils
import kotlinx.coroutines.launch

val LocalPlatformContext = staticCompositionLocalOf<Any?> { null }

@Composable
@Preview
fun SongwritersNotebookApp(
    mainViewModel: MainViewModel = viewModel { MainViewModel() },
    onGoogleSignIn: () -> Unit = {}
) {
    val settingsViewModel: SettingsViewModel = viewModel { SettingsViewModel() }
    val isDarkMode by settingsViewModel.isDarkMode.collectAsState()
    val textSize by settingsViewModel.textSize.collectAsState()

    SongwritersNotebookTheme(darkTheme = isDarkMode) {
        val navController = rememberNavController()
        val databaseReady by mainViewModel.databaseReady.collectAsState()
        
        val onSignInRequested = {
            navController.navigate(AppRoutes.AUTH_SCREEN)
        }

        if (!databaseReady) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            return@SongwritersNotebookTheme
        }

        val isMaintenanceMode by mainViewModel.isMaintenanceMode.collectAsState()
        if (isMaintenanceMode) {
            songwritersnotebook.app.screens.MaintenanceScreen()
            return@SongwritersNotebookTheme
        }

        val showMigrationPrompt by mainViewModel.showAnonymousMigrationPrompt.collectAsState()
        val isSessionLimitExceeded by mainViewModel.isSessionLimitExceeded.collectAsState()
        val userProfile by mainViewModel.userProfile.collectAsState()
        if (isSessionLimitExceeded) {
            songwritersnotebook.app.components.SessionLimitDialog(
                userProfile = userProfile,
                currentDeviceId = mainViewModel.deviceId,
                onDismissRequest = { mainViewModel.dismissSessionLimitDialog() },
                onRemoveSession = { deviceId -> mainViewModel.removeSession(deviceId) }
            )
        }

        val isWeb = remember { getPlatform().name.contains("Web", ignoreCase = true) }
        
        // --- Tutorial Navigation Logic ---
        songwritersnotebook.app.tutorial.TutorialNavigator(navController)
        
        // --- URL Synchronization Logic ---
        SyncWebUrl(navController)

        val currentUserState by mainViewModel.currentUser.collectAsState()
        
        // Start on Home for Web (Frictionless Onboarding), otherwise use Home/Auth logic
        val startRoute = remember(userProfile) {
            if (userProfile != null) {
                AppRoutes.HOME_SCREEN
            } else {
                val initialPath = PlatformUtils().getInitialRoute() ?: ""
                when {
                    initialPath.contains("subscription") || initialPath.contains("pricing") -> AppRoutes.SUBSCRIPTION_SCREEN
                    initialPath.contains("roadmap") -> AppRoutes.ROADMAP_SCREEN
                    initialPath.contains("privacy") -> AppRoutes.PRIVACY_POLICY_SCREEN
                    initialPath.contains("terms") -> AppRoutes.TERMS_SCREEN
                    else -> AppRoutes.AUTH_SCREEN
                }
            }
        }

        // Global Authentication Guard: Redirect to Auth if trying to access protected screens while logged out
        LaunchedEffect(currentUserState) {
            val currentRoute = navController.currentBackStackEntry?.destination?.route ?: ""
            val protectedRoutes = if (isWeb) {
                // On Web, allow unauthenticated access to Home and Studio for Guest Sessions
                listOf(AppRoutes.SETTINGS_SCREEN)
            } else {
                listOf(
                    AppRoutes.HOME_SCREEN,
                    AppRoutes.SETTINGS_SCREEN,
                    AppRoutes.EDITOR_SCREEN,
                    AppRoutes.STUDIO_SCREEN
                )
            }
            
            val isProtected = protectedRoutes.any { currentRoute.startsWith(it) }

            if (currentUserState == null && isProtected) {
                // Not logged in and on a protected screen: Go to Auth
                navController.navigate(AppRoutes.AUTH_SCREEN) {
                    popUpTo(0) { inclusive = true }
                }
            } else if (currentUserState != null && currentRoute == AppRoutes.AUTH_SCREEN) {
                // Just logged in: Go to Home
                navController.navigate(AppRoutes.HOME_SCREEN) {
                    popUpTo(AppRoutes.AUTH_SCREEN) { inclusive = true }
                }
            }
        }

        LaunchedEffect(Unit) {
            TutorialManager.tutorialFinished.collect {
                // Force navigation to Home and clear stack to ensure we don't stay in Editor/Studio with deleted song
                navController.navigate(AppRoutes.HOME_SCREEN) {
                    popUpTo(0) { inclusive = true } // Clear everything
                }
                // We need to reload songs. We can't access HomeViewModel easily here as it's scoped to composable.
                // But MainViewModel has signInUser which reloads. Or we can rely on SongRepository.
                // A clean way is to trigger a refresh in HomeViewModel when it composes next, or use a shared event.
                // Let's use MainViewModel to trigger a global refresh or just let HomeViewModel init do it (since we pop back).
                // Actually, popping back to Home re-composes HomeViewModel? No, it might reuse it if scoped to nav graph root.
                // But `viewModel()` in composable usually scopes to the destination.
                // If we pop everything, we re-enter HOME_SCREEN.
            }
        }
        
        if (showMigrationPrompt) {
            AlertDialog(
                onDismissRequest = {},
                title = { Text("Sync Local Songs?") },
                text = { Text("We found songs created on this device before you signed in. Would you like to merge them into your account, or delete them to start fresh?") },
                confirmButton = {
                    Button(onClick = { mainViewModel.mergeAnonymousSongs() }) {
                        Text("Merge & Sync")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { mainViewModel.declineAnonymousSongs() }) {
                        Text("Delete Local Data")
                    }
                }
            )
        }
        
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = MaterialTheme.colorScheme.background
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .pointerInput(Unit) {
                        detectTapGestures {
                            PlatformDependencyProvider().resumeAudioContext()
                        }
                    }
            ) {
                NavHost(
                    navController = navController,
                    startDestination = startRoute
                ) {

                    composable(AppRoutes.AUTH_SCREEN) {
                        AuthScreen(
                            onBack = { navController.popBackStack() },
                            onSuccess = { 
                                navController.navigate(AppRoutes.HOME_SCREEN) {
                                    popUpTo(AppRoutes.AUTH_SCREEN) { inclusive = true }
                                }
                            },
                            mainViewModel = mainViewModel,
                            onGoogleSignIn = onGoogleSignIn,
                            onFeatures = { PlatformUtils().openUrl("/features") },
                            onPricing = { navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN) },
                            onRoadmap = { navController.navigate(AppRoutes.ROADMAP_SCREEN) },
                            onHome = {
                                PlatformUtils().openUrl("/")
                            }
                        )
                    }

                    composable(AppRoutes.HOME_SCREEN) {
                        val homeViewModel: HomeViewModel = viewModel { HomeViewModel() }
                        val uiState by homeViewModel.uiState.collectAsState()
                        val filteredSongs by homeViewModel.filteredSongs.collectAsState()
                        val subscriptionTier by mainViewModel.currentSubscriptionTier.collectAsState()
                        val currentUser by mainViewModel.currentUser.collectAsState()
                        val context = LocalPlatformContext.current
                        
                        LaunchedEffect(currentUser?.uid) {
                            homeViewModel.loadSongs()
                        }
                        
                        HomeScreen(
                            songs = filteredSongs,
                            isLoading = uiState.isLoading,
                            error = uiState.error,
                            onEditSong = { song ->
                                if (context != null) {
                                    songwritersnotebook.app.utils.InterstitialAdManager.showAd(context) {
                                        navController.navigate("${AppRoutes.EDITOR_SCREEN}/${song.id}")
                                    }
                                } else {
                                    navController.navigate("${AppRoutes.EDITOR_SCREEN}/${song.id}")
                                }
                            },
                            onStudioSong = { song ->
                                if (context != null) {
                                    songwritersnotebook.app.utils.InterstitialAdManager.showAd(context) {
                                        navController.navigate("${AppRoutes.STUDIO_SCREEN}/${song.id}")
                                    }
                                } else {
                                    navController.navigate("${AppRoutes.STUDIO_SCREEN}/${song.id}")
                                }
                            },
                            onNavigateToSettings = {
                                navController.navigate(AppRoutes.SETTINGS_SCREEN)
                            },
                            onNavigateToTutorial = {
                                navController.navigate(AppRoutes.TUTORIAL_SCREEN)
                            },
                            onDeleteSong = { songId ->
                                homeViewModel.onSongDeleteRequest(filteredSongs.find { it.id == songId } ?: return@HomeScreen)
                            },
                            onRefresh = {
                                homeViewModel.loadSongs()
                            },
                            onExportText = { song ->
                                homeViewModel.onSongToTextRequest(song, subscriptionTier)
                            },
                            onExportAudio = { song ->
                                homeViewModel.onAudioExportRequest(song, subscriptionTier)
                            },
                            viewModel = homeViewModel,
                            searchQuery = uiState.searchQuery,
                            onSearchQueryChange = { homeViewModel.onSearchQueryChange(it) },
                            subscriptionTier = subscriptionTier,
                            onGoogleSignIn = onSignInRequested,
                            onNavigateToSubscription = {
                                navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN)
                            }
                        )
                    }
                    
                    composable("${AppRoutes.EDITOR_SCREEN}/{songId}") { backStackEntry ->
                        val songId = backStackEntry.arguments?.getString("songId")
                        val editorViewModel: EditorViewModel = viewModel { EditorViewModel() }
                        val editorUiState by editorViewModel.uiState.collectAsState()
                        val userProfile by mainViewModel.userProfile.collectAsState()
                        val subscriptionTier by mainViewModel.currentSubscriptionTier.collectAsState()
                        val productDetailsState by mainViewModel.productDetailsState.collectAsState()
                        val context = LocalPlatformContext.current
                        
                        LaunchedEffect(songId) {
                            if (songId != null) {
                                editorViewModel.loadSongById(songId, subscriptionTier)
                            }
                        }

                        LaunchedEffect(subscriptionTier) {
                            editorViewModel.updateSubscriptionTier(subscriptionTier)
                        }
                        
                        EditorScreen(
                            song = editorUiState.song,
                            userProfile = userProfile,
                            subscriptionTier = subscriptionTier,
                            onBack = {
                                navController.navigate(AppRoutes.HOME_SCREEN) { popUpTo(0) }
                            },
                            onSave = { song ->
                                editorViewModel.saveSong()
                                navController.navigate(AppRoutes.HOME_SCREEN) { popUpTo(0) }
                            },
                            onNavigateToStudio = { song ->
                                navController.navigate("${AppRoutes.STUDIO_SCREEN}/${song.id}")
                            },
                            onNavigateToSubscription = {
                                navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN)
                            },
                            aiCreditProducts = productDetailsState.productDetailsList
                                .filter { BillingClientWrapper.AI_CREDIT_PRODUCT_IDS.contains(it.productId) }
                                .sortedBy { BillingClientWrapper.AI_CREDIT_PRODUCT_IDS.indexOf(it.productId) },
                            onPurchaseCreditPack = { product ->
                                mainViewModel.currentUser.value?.uid?.let { userId ->
                                    if (context != null) {
                                        mainViewModel.billingClientWrapper.launchPurchaseFlow(context, product, userId)
                                    }
                                }
                            },
                            viewModel = editorViewModel,
                            fontSize = textSize.fontSize
                        )
                    }
                    
                    composable("${AppRoutes.STUDIO_SCREEN}/{songId}") { backStackEntry ->
                        val songId = backStackEntry.arguments?.getString("songId") ?: ""
                        val studioViewModel: StudioViewModel = viewModel { StudioViewModel() }
                        val studioUiState by studioViewModel.uiState.collectAsState()
                        val subscriptionTier by mainViewModel.currentSubscriptionTier.collectAsState()
                        
                        StudioScreen(
                            song = studioUiState.song,
                            songId = songId,
                            onBack = {
                                navController.navigate("${AppRoutes.EDITOR_SCREEN}/$songId") { popUpTo(0) }
                            },
                            viewModel = studioViewModel,
                            subscriptionTier = subscriptionTier,
                            onNavigateToSubscription = {
                                navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN)
                            }
                        )
                    }
                    
                    composable(AppRoutes.SETTINGS_SCREEN) {
                        SettingsScreen(
                            onBack = {
                                if (!navController.popBackStack()) {
                                    navController.navigate(AppRoutes.HOME_SCREEN) { popUpTo(0) }
                                }
                            },
                            settingsViewModel = settingsViewModel,
                            mainViewModel = mainViewModel,
                            onNavigateToSubscription = {
                                navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN)
                            },
                            onNavigateToRoadmap = {
                                navController.navigate(AppRoutes.ROADMAP_SCREEN)
                            },
                            onNavigateToPrivacyPolicy = {
                                navController.navigate(AppRoutes.PRIVACY_POLICY_SCREEN)
                            },
                            onSignIn = {
                                onSignInRequested()
                            },
                            onNavigateToHome = {
                                navController.navigate(AppRoutes.HOME_SCREEN) {
                                    popUpTo(AppRoutes.AUTH_SCREEN) { inclusive = true }
                                }
                            }
                        )
                    }
                    
                    composable(AppRoutes.SUBSCRIPTION_SCREEN) {
                        SubscriptionScreen(
                            onBack = {
                                if (!navController.popBackStack()) {
                                    navController.navigate(AppRoutes.HOME_SCREEN) { popUpTo(0) }
                                }
                            },
                            onFeatures = { PlatformUtils().openUrl("/features") },
                            onPricing = { /* Already here */ },
                            onRoadmap = { navController.navigate(AppRoutes.ROADMAP_SCREEN) },
                            onHome = {
                                PlatformUtils().openUrl("/")
                            },
                            onPrivacy = { navController.navigate(AppRoutes.PRIVACY_POLICY_SCREEN) },
                            onTerms = { navController.navigate(AppRoutes.TERMS_SCREEN) },
                            onOpenUrl = { url -> PlatformUtils().openUrl(url) },
                            onSignIn = onSignInRequested,
                            mainViewModel = mainViewModel
                        )
                    }
                    
                    composable(AppRoutes.TUTORIAL_SCREEN) {
                        TutorialScreen(
                            onBack = { navController.popBackStack() },
                            onComplete = { navController.popBackStack() }
                        )
                    }
                    
                    composable(AppRoutes.PRIVACY_POLICY_SCREEN) {
                        PrivacyPolicyScreen(
                            onBack = { navController.popBackStack() }
                        )
                    }

                    composable(AppRoutes.ROADMAP_SCREEN) {
                        val roadmapViewModel: RoadmapViewModel = viewModel { RoadmapViewModel() }
                        val userProfile by mainViewModel.userProfile.collectAsState()
                        RoadmapScreen(
                            onBack = { navController.popBackStack() },
                            onFeatures = { PlatformUtils().openUrl("/features") },
                            onPricing = { navController.navigate(AppRoutes.SUBSCRIPTION_SCREEN) },
                            onRoadmap = { /* Already here */ },
                            onHome = {
                                PlatformUtils().openUrl("/")
                            },
                            onPrivacy = { navController.navigate(AppRoutes.PRIVACY_POLICY_SCREEN) },
                            onTerms = { navController.navigate(AppRoutes.TERMS_SCREEN) },
                            onOpenUrl = { url -> PlatformUtils().openUrl(url) },
                            viewModel = roadmapViewModel,
                            isAdmin = userProfile?.isAdmin ?: false,
                            mainViewModel = mainViewModel,
                            onSignIn = onSignInRequested
                        )
                    }

                    composable(AppRoutes.TERMS_SCREEN) {
                        TermsOfServiceScreen(
                            onBack = { navController.popBackStack() }
                        )
                    }
                }
                
                if (isWeb) {
                    val navBackStackEntry by navController.currentBackStackEntryAsState()
                    val currentRoute = navBackStackEntry?.destination?.route
                    
                    if (currentRoute == AppRoutes.SETTINGS_SCREEN) {
                        Box(modifier = Modifier.fillMaxWidth().align(Alignment.BottomCenter)) {
                            WebFooter(
                                onNavigateToPrivacy = { navController.navigate(AppRoutes.PRIVACY_POLICY_SCREEN) },
                                onNavigateToTerms = { navController.navigate(AppRoutes.TERMS_SCREEN) },
                                isCompact = true
                            )
                        }
                    }
                }
                
                // Tutorial Overlay sits on top of everything
                TutorialOverlay(isGlobal = true)
            }
        }
    }
}
